import { createAsyncThunk } from '@reduxjs/toolkit';
import deepEqual from 'deep-equal';
import axios from 'axios';

import { somethingWrongNotification } from '../notifier/notifierActions';
import sendRequest from '../../service/sendRequest';
import callApi from '../../service/callApi';
import mapDatasetToDto from './mapDatasetToDto';
import { clearCrawledUrls } from './createSonarReducer';
import errorNotification from '../../utils/errorNotification';
import { updateDatasetOptions } from '../sonar-management/sonarManagementReducer';
import updateNewSocialsStatuses from '../../utils/updateNewSocialsStatuses';

export const IMPORT_DATASET = 'createSonar/IMPORT_DATASET';
export const INIT_SONAR_PROJECT = 'createSonar/INIT_SONAR_PROJECT';
export const EDIT_SONAR_PROJECT = 'createSonar/EDIT_SONAR_PROJECT';
const EDIT_SONAR_PROJECT_USERS = 'createSonar/EDIT_SONAR_PROJECT_USERS';
export const CANCEL_CREATION = 'createSonar/CANCEL_CREATION';
export const ADD_DATASET = 'createSonar/ADD_DATASET';
const EDIT_DATASET = 'createSonar/EDIT_DATASET';
export const CRAWL_SINGLE_MEDIA = 'createSonar/CRAWL_SINGLE_MEDIA';
export const CRAWL_ALL_MEDIAS = 'createSonar/CRAWL_ALL_MEDIAS';
export const DELETE_FROM_DATASET = 'createSonar/DELETE_FROM_DATASET';
export const DELETE_SELECTED_ITEMS = 'createSonar/DELETE_SELECTED_ITEMS';

export const importDataset = createAsyncThunk(IMPORT_DATASET, async (file, { dispatch, getState, rejectWithValue }) => {
  try {
    const { id } = getState().createSonar.projectInfo;

    if (!id) {
      throw new Error();
    }

    const formData = new FormData();
    formData.append('file', file, file.name);

    const response = await callApi.request({
      url: `/api/sonar-data/init/${id}`,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      method: 'POST',
      data: formData,
    });

    return response.data;
  } catch (e) {
    if (e.response.status === 400) {
      return rejectWithValue(e.response.data);
    } else {
      dispatch(somethingWrongNotification());
      return rejectWithValue({});
    }
  }
});

export const initSonarProject = createAsyncThunk(INIT_SONAR_PROJECT, async (_, { dispatch }) => {
  const initSonar = await sendRequest(() => callApi.post(`/api/sonar/init`, {}), {
    successStatusCode: 201,
  });

  if (initSonar.data.id) {
    const initSonarDataset = await sendRequest(() =>
      callApi.post(`/api/sonar-data/search/init?sort=-count`, {
        projectId: initSonar.data.id,
      }),
    );

    return { projectInfo: initSonar.data, dataset: initSonarDataset.data };
  }
});

export const cancelCreation = createAsyncThunk(CANCEL_CREATION, async (_, { getState }) => {
  const projectInfo = getState().createSonar.projectInfo;

  if (!projectInfo.id) {
    throw new Error();
  }

  await sendRequest(() => callApi.delete(`/api/sonar/${projectInfo.id}`));
});

export const editCreateSonarProject = createAsyncThunk(
  EDIT_SONAR_PROJECT,
  async ({ projectName, startingFromMonth, startingFromYear, brand, formed, rangeType }, { getState }) => {
    const projectInfo = getState().createSonar.projectInfo;

    if (!projectInfo.id || !projectName) {
      throw new Error();
    }

    const newQuery = {
      projectName,
      brand,
      startDate: `${startingFromYear}-${startingFromMonth}-01`,
      rangeType,
      ...(formed ? { formed } : {}),
    };

    const oldQuery = {
      projectName: projectInfo.projectName,
      brand: projectInfo.brand,
      startDate: projectInfo.startDate,
      rangeType: projectInfo.rangeType,
    };

    if (!deepEqual(newQuery, oldQuery)) {
      const response = await sendRequest(() => callApi.post(`/api/sonar/edit/${projectInfo.id}`, newQuery), {
        onError: (e) => {
          errorNotification(e, [
            { code: 409, errorText: 'projectWithThisNameAlreadyExist', errorSubText: 'pleaseChangeTheProjectName' },
          ]);
        },
      });

      return response.data;
    } else {
      throw new Error();
    }
  },
);

export const editCreateSonarProjectUsers = createAsyncThunk(EDIT_SONAR_PROJECT_USERS, async (users, { getState }) => {
  const projectInfo = getState().createSonar.projectInfo;

  if (!projectInfo.id) {
    throw new Error();
  }

  await sendRequest(() =>
    callApi.post(`/api/project/edit/users/${projectInfo.id}`, {
      userIds: users.map(({ id }) => id),
    }),
  );
});

export const addDataset = createAsyncThunk(ADD_DATASET, async (values, { getState, dispatch, rejectWithValue }) => {
  const { id } = getState().createSonar.projectInfo;
  const crawledUrls = getState().createSonar.crawledUrls;

  if (!id) {
    dispatch(somethingWrongNotification());
    return rejectWithValue(null);
  }

  const datasetDto = mapDatasetToDto(id, values, crawledUrls);
  const response = await sendRequest(() => callApi.post(`/api/sonar-data/add`, datasetDto));

  dispatch(clearCrawledUrls());

  dispatch(updateDatasetOptions([response.data]));

  return response.data;
});

export const crawlSingleMedia = createAsyncThunk(
  CRAWL_SINGLE_MEDIA,
  async ({ socialMedia, domain }, { getState, dispatch, rejectWithValue }) => {
    const crawlServerUrl = getState().securityNew.crawlServerUrl;

    if (!domain || !socialMedia || !crawlServerUrl) {
      dispatch(somethingWrongNotification());
      return rejectWithValue(null);
    }

    const response = await sendRequest(
      () => axios.get(`${crawlServerUrl}/api/scrapper/sonar/social?domain=${domain}&linkType=${socialMedia}`),
      {
        onError: (e) => {
          rejectWithValue(e);
        },
      },
    );

    return { [socialMedia]: response.data };
  },
);

export const crawlAllMedia = createAsyncThunk(
  CRAWL_ALL_MEDIAS,
  async ({ domain }, { getState, dispatch, rejectWithValue }) => {
    const crawlServerUrl = getState().securityNew.crawlServerUrl;

    if (!domain || !crawlServerUrl) {
      dispatch(somethingWrongNotification());
      return rejectWithValue(null);
    }

    const response = await sendRequest(() => axios.post(`${crawlServerUrl}/api/scrapper/sonar/socials`, [domain]), {
      onError: (e) => {
        rejectWithValue(e);
      },
    });

    return { ...response.data[domain] };
  },
);

export const deleteFromDataset = createAsyncThunk(DELETE_FROM_DATASET, async (ids, { rejectWithValue }) => {
  await sendRequest(() => axios.delete(`/api/sonar-data`, { data: ids }), {
    onError: (e) => {
      rejectWithValue(e);
    },
  });

  return ids;
});

export const deleteSelectedItems = createAsyncThunk(DELETE_SELECTED_ITEMS, async (_, { dispatch, getState }) => {
  const selectedItems = getState().createSonar.dataset.filter(({ isSelected }) => isSelected);
  const selectedItemsIds = selectedItems.map(({ id }) => id);

  dispatch(deleteFromDataset(selectedItemsIds));
});

export const editDataset = createAsyncThunk(
  EDIT_DATASET,
  async ({ values, datasetId }, { getState, dispatch, rejectWithValue }) => {
    const { id } = getState().createSonar.projectInfo;
    const crawledUrls = getState().createSonar.crawledUrls;
    const editableDataset = getState().sonarManagement.editableDataset;

    if (!id) {
      dispatch(somethingWrongNotification());
      return rejectWithValue(null);
    }

    const datasetDto = mapDatasetToDto(id, values, crawledUrls);
    const datasetDtoWithUpdatedSocials = updateNewSocialsStatuses(editableDataset, datasetDto);

    const response = await sendRequest(
      () => callApi.post(`/api/sonar-data/edit?id=${datasetId}`, datasetDtoWithUpdatedSocials),
      {
        onError: () => {
          dispatch(somethingWrongNotification());
          return rejectWithValue(null);
        },
        onSuccess: () => {
          dispatch(updateDatasetOptions([datasetDto]));
        },
      },
    );

    dispatch(clearCrawledUrls());

    return response.data;
  },
);
